import React, { useState, useEffect } from 'react';
import { Container, Card, Row, Col, Form, Button, Spinner, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { eventService } from '../services/api';

function EventForm({ 
  formData: initialFormData, 
  handleInputChange: externalHandleInputChange, 
  handleSubmit: externalHandleSubmit, 
  categories: initialCategories, 
  loading: externalLoading, 
  error: externalError,
  submitButtonText = 'Créer l\'événement',
  isEditing = false
}) {
  const navigate = useNavigate();
  
  // Get user data from localStorage to extract partner information
  const getUserPartner = () => {
    try {
      
      // First check if admin_for_partner is directly stored in localStorage
      const userData = localStorage.getItem('user');
      const parsedData = JSON.parse(userData); // Parse the string into an object
      return parsedData?.partner || null;
    } catch (error) {
      console.error('Error parsing user data from localStorage:', error);
      return '';
    }
  };

  // Get user data from localStorage to extract partner information
  const getUserPartnerId = () => {
    try {
      
      // First check if admin_for_partner is directly stored in localStorage
      const userData = localStorage.getItem('user');
      const parsedData = JSON.parse(userData); // Parse the string into an object
      return parsedData?.partner_id || null;
    } catch (error) {
      console.error('Error parsing user data from localStorage:', error);
      return '';
    }
  };
  
  const [formData, setFormData] = useState(initialFormData || {
    title: '',
    description: '',
    date: '',
    time: '',
    location: '',
    wilaya: '16', // Default to Alger
    category: '',
    price: '',
    available_seats: '',
    image: null,
    is_active: true,
    is_featured: false,
    registration_closed: false,
    partner: getUserPartner() // Use the function to get partner from user data
   
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState(initialCategories || []);

  // Fetch categories if not provided
  useEffect(() => {
    if (!initialCategories) {
      fetchCategories();
    }
  }, [initialCategories]);

  const fetchCategories = async () => {
    try {
      const response = await eventService.getEventCategories();
      setCategories(response.data);
    } catch (err) {
      console.error('Error fetching categories:', err);
      setError('Erreur lors du chargement des catégories');
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    if (externalHandleInputChange) {
      externalHandleInputChange(e);
      return;
    }

    const { name, value, type, checked, files } = e.target;
    
    if (type === 'file') {
      setFormData({
        ...formData,
        [name]: files[0]
      });
    } else if (type === 'checkbox') {
      setFormData({
        ...formData,
        [name]: checked
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (externalHandleSubmit) {
      externalHandleSubmit(e);
      return;
    }

    setLoading(true);
    
    try {
      const eventData = new FormData();
      
      // Validate required fields
      if (!formData.title || !formData.date || !formData.time || !formData.location) {
        throw new Error('Veuillez remplir tous les champs obligatoires');
      }
      
      console.log('Form data before submission:', formData);
      
      // Append form data
      eventData.append('title', formData.title);
      eventData.append('description', formData.description || '');
      
      // Combine date and time for date_time field
      const dateTime = `${formData.date}T${formData.time}:00`;
      eventData.append('date_time', dateTime);
      
      // Also send the date separately since the backend requires it
      eventData.append('date', formData.date);
      
      console.log('Formatted date_time:', dateTime);
      
      eventData.append('location', formData.location);
      eventData.append('wilaya', formData.wilaya || '');
      
      if (formData.category) {
        eventData.append('category', formData.category);
      } 
      
      if (formData.price) {
        eventData.append('price', formData.price);
      }
      
      if (formData.available_seats) {
        eventData.append('available_seats', formData.available_seats);
      }
      
      eventData.append('is_active', true); // Default to active
      eventData.append('is_featured', formData.is_featured);
      eventData.append('registration_closed', formData.registration_closed);
      
      // Add partner to form data - FIXED: Call the function to get the value
      const partnerId = getUserPartnerId();
      eventData.append('partner', partnerId);
      
      // Add image to form data if it exists and is a valid file
      if (formData.image && formData.image instanceof File) {
        console.log('Adding image to form data:', formData.image.name);
        eventData.append('image', formData.image, formData.image.name);
      }
      
      let response;
      
      if (isEditing && formData.id) {
        console.log('Updating event with ID:', formData.id);
        response = await eventService.updateEvent(formData.id, eventData);
      } else {
        console.log('Creating new event');
        response = await eventService.createEvent(eventData);
      }
      
      console.log('API response:', response);
      
      // Redirect back to events management
      navigate('/cpanel/events');
      
    } catch (err) {
      console.error('Error details:', err);
      
      // Handle API error response
      if (err.response) {
        console.error('API error response:', err.response.status, err.response.data);
        
        // Format error message from API response if available
        if (err.response.data) {
          if (typeof err.response.data === 'string') {
            setError(err.response.data);
          } else if (typeof err.response.data === 'object') {
            const errorMessages = [];
            for (const key in err.response.data) {
              errorMessages.push(`${key}: ${err.response.data[key]}`);
            }
            setError(errorMessages.join(', '));
          }
        } else {
          setError(`Error ${err.response.status}: ${err.response.statusText}`);
        }
      } else {
        setError(err.message || 'Erreur lors de la création de l\'événement. Veuillez réessayer.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="py-4">
      <Card>
        <Card.Header>
          <h4>{isEditing ? 'Modifier l\'événement' : 'Ajouter un nouvel événement'}</h4>
        </Card.Header>
        <Card.Body>
          {/* Note: encType is not needed in React forms as FormData handles this */}
          <Form onSubmit={handleSubmit} encType="multipart/form-data">
            {(error || externalError) && <Alert variant="danger">{error || externalError}</Alert>}
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Titre *</Form.Label>
                  <Form.Control
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Partenaire</Form.Label>
                  <Form.Control
                    type="text"
                    name="partner"
                    value={formData.partner}
                    readOnly
                  />
                  <Form.Text className="text-muted">
                    Partenaire associé à cet événement
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Catégorie</Form.Label>
                  <Form.Select
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                  >
                    <option value="">Sélectionner une catégorie</option>
                    {categories.map(category => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Date *</Form.Label>
                  <Form.Control
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Heure *</Form.Label>
                  <Form.Control
                    type="time"
                    name="time"
                    value={formData.time}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Lieu *</Form.Label>
                  <Form.Control
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Wilaya</Form.Label>
                  <Form.Select
                    name="wilaya"
                    value={formData.wilaya}
                    onChange={handleInputChange}
                  >
                    <option value="01">Adrar</option>
                    <option value="02">Chlef</option>
                    <option value="03">Laghouat</option>
                    <option value="04">Oum El Bouaghi</option>
                    <option value="05">Batna</option>
                    <option value="06">Béjaïa</option>
                    <option value="07">Biskra</option>
                    <option value="08">Béchar</option>
                    <option value="09">Blida</option>
                    <option value="10">Bouira</option>
                    <option value="11">Tamanrasset</option>
                    <option value="12">Tébessa</option>
                    <option value="13">Tlemcen</option>
                    <option value="14">Tiaret</option>
                    <option value="15">Tizi Ouzou</option>
                    <option value="16">Alger</option>
                    <option value="17">Djelfa</option>
                    <option value="18">Jijel</option>
                    <option value="19">Sétif</option>
                    <option value="20">Saïda</option>
                    <option value="21">Skikda</option>
                    <option value="22">Sidi Bel Abbès</option>
                    <option value="23">Annaba</option>
                    <option value="24">Guelma</option>
                    <option value="25">Constantine</option>
                    <option value="26">Médéa</option>
                    <option value="27">Mostaganem</option>
                    <option value="28">M'Sila</option>
                    <option value="29">Mascara</option>
                    <option value="30">Ouargla</option>
                    <option value="31">Oran</option>
                    <option value="32">El Bayadh</option>
                    <option value="33">Illizi</option>
                    <option value="34">Bordj Bou Arréridj</option>
                    <option value="35">Boumerdès</option>
                    <option value="36">El Tarf</option>
                    <option value="37">Tindouf</option>
                    <option value="38">Tissemsilt</option>
                    <option value="39">El Oued</option>
                    <option value="40">Khenchela</option>
                    <option value="41">Souk Ahras</option>
                    <option value="42">Tipaza</option>
                    <option value="43">Mila</option>
                    <option value="44">Aïn Defla</option>
                    <option value="45">Naâma</option>
                    <option value="46">Aïn Témouchent</option>
                    <option value="47">Ghardaïa</option>
                    <option value="48">Relizane</option>
                    <option value="49">El M'Ghair</option>
                    <option value="50">El Meniaa</option>
                    <option value="51">Ouled Djellal</option>
                    <option value="52">Bordj Baji Mokhtar</option>
                    <option value="53">Béni Abbès</option>
                    <option value="54">Timimoun</option>
                    <option value="55">Touggourt</option>
                    <option value="56">Djanet</option>
                    <option value="57">In Salah</option>
                    <option value="58">In Guezzam</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Prix (DA)</Form.Label>
                  <Form.Control
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleInputChange}
                    min="0"
                    step="0.01"
                  />
                  <Form.Text className="text-muted">
                    Laissez vide pour un événement gratuit
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Nombre de places disponibles</Form.Label>
                  <Form.Control
                    type="number"
                    name="available_seats"
                    value={formData.available_seats}
                    onChange={handleInputChange}
                    min="0"
                  />
                  <Form.Text className="text-muted">
                    Laissez vide pour un nombre illimité
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={4}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Image de l'événement {!isEditing && '*'}</Form.Label>
              <Form.Control
                type="file"
                name="image"
                accept="image/jpeg,image/png,image/gif"
                onChange={handleInputChange}
                required={false}
              />
          
              <Form.Text className="text-muted">
                Formats acceptés: JPG, PNG, GIF. Taille max: 5MB
              </Form.Text>
            </Form.Group>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label="Mettre en vedette"
                    name="is_featured"
                    checked={formData.is_featured}
                    onChange={handleInputChange}
                  />
                  <Form.Text className="text-muted">
                    Les événements en vedette apparaissent sur la page d'accueil
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label="Fermer les inscriptions"
                    name="registration_closed"
                    checked={formData.registration_closed}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <div className="d-flex justify-content-between mt-4">
              <Button variant="secondary" onClick={() => navigate('/cpanel/events')}>
                Annuler
              </Button>
              <Button variant="primary" type="submit" disabled={loading || externalLoading}>
                {(loading || externalLoading) ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" className="me-2" />
                    {isEditing ? 'Mise à jour...' : 'Création...'}
                  </>
                ) : (
                  submitButtonText
                )}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
}

export default EventForm;